/**
 * Matching a tour to the current route.
 *
 * Tours were keyed by exact pathname, which silently stranded every step
 * aimed at a *detail* page: seven `data-tour` tags (`queues-send`,
 * `queues-receive`, `queues-stats`, `messaging-grants`, `topics-publish`,
 * `topics-subscriptions`, `functions-invoke`) sat in the DOM with no tour
 * that could ever match `/queues/<uuid>`. Keys may now carry `:param`
 * segments, and the most specific match wins so `/queues` and `/queues/:id`
 * can both have their own tour.
 */
import { CLOUD_BASE, SASE_BASE } from "@/lib/sectionBase";

import { TOURS, type Tour } from "./tours";

/** A shared module's SASE door runs the cloud door's tour: the same page,
 *  the same `data-tour` tags (SD-01, SASE-18). */
const DOOR_ALIASES: [string, string][] = [
  [SASE_BASE.privateAccess, CLOUD_BASE.privateAccess],
  [SASE_BASE.agentGateways, CLOUD_BASE.agentGateways],
];

/** The cloud door's path for a SASE door's path; any other path unchanged. */
export function cloudDoorPath(pathname: string): string {
  for (const [door, cloud] of DOOR_ALIASES) {
    if (pathname === door || pathname.startsWith(`${door}/`)) {
      return cloud + pathname.slice(door.length);
    }
  }
  return pathname;
}

/** Does `pattern` (with optional `:param` segments) match `pathname`? */
export function matchesRoute(pattern: string, pathname: string): boolean {
  if (pattern === pathname) return true;
  const patternParts = pattern.split("/").filter(Boolean);
  const pathParts = pathname.split("/").filter(Boolean);
  if (patternParts.length !== pathParts.length) return false;
  return patternParts.every(
    (part, i) => part.startsWith(":") || part === pathParts[i],
  );
}

/** Literal (non-`:param`) segments — used to rank competing patterns. */
function specificity(pattern: string): number {
  return pattern
    .split("/")
    .filter((part) => part && !part.startsWith(":")).length;
}

/** The `TOURS` key a pathname runs: exact key first, then the most specific pattern. */
function tourKeyForPath(requested: string): string | null {
  const pathname = TOURS[requested] ? requested : cloudDoorPath(requested);
  if (TOURS[pathname]) return pathname;
  const matches = Object.keys(TOURS)
    .filter((pattern) => matchesRoute(pattern, pathname))
    .sort((a, b) => specificity(b) - specificity(a));
  return matches[0] ?? null;
}

/** The tour for a pathname: exact key first, then the most specific pattern. */
export function tourForPath(requested: string): Tour | null {
  const key = tourKeyForPath(requested);
  return key ? (TOURS[key] ?? null) : null;
}

/** UI-03: a tour starts by itself only on a list or landing page (a key with
 *  no `:param`). One resource's page is where a customer is reading or
 *  changing something; its tour runs from the topbar button alone. */
export function tourAutoStarts(requested: string): boolean {
  const key = tourKeyForPath(requested);
  return key !== null && !key.includes(":");
}
